const PAYMENT_TYPE_MAPPINGS = {
  client_payment: {
    collectionType: (description: string) => (description.includes("PrePayment") ? "Client Pre-Payment" : "Client Payment"),
    paymentType: "Credit Card",
  },
  operator_refund: {
    collectionType: () => "Refund",
    paymentType: "Bank Debit",
  },
  operator_payment: {
    collectionType: () => "Operator Payment",
    paymentType: "Bank Debit (Invoiced Booking)",
  },
} as const;

export const getCollectionType = (type: string, description: string): string => {
  const mapping = PAYMENT_TYPE_MAPPINGS[type as keyof typeof PAYMENT_TYPE_MAPPINGS];
  return mapping ? mapping.collectionType(description) : "";
};

export const getPaymentType = (type: string): string => {
  const mapping = PAYMENT_TYPE_MAPPINGS[type as keyof typeof PAYMENT_TYPE_MAPPINGS];
  return mapping ? mapping.paymentType : "";
};

const getPaymentTypeFromDescription = (description: string): string => {
  if (description.includes("Credit Card Processing Fee")) {
    return description.includes("Intl.") ? "Intl. CC Processing Fee" : "Domestic CC Processing Fee";
  }

  if (description.includes("reversal")) {
    if (description.includes("Driver Payment")) return "Driver Payment Reversal";
    if (description.includes("Platform Payment")) return "Platform Payment Reversal";
    if (description.includes("Provider Payment")) return "Provider Payment Reversal";
    return "Payment Reversal";
  }

  if (description.includes("adjustment")) {
    if (description.includes("Driver Payment")) return "Driver Payment Adjustment";
    if (description.includes("Platform Payment")) return "Platform Payment Adjustment";
    if (description.includes("Provider Payment")) return "Provider Payment Adjustment";
    return "Payment Adjustment";
  }

  if (description.includes("Platform Payment")) return "Platform Booking Fee";
  if (description.includes("Offload")) return "Platform Offload Fee";
  if (description.includes("Provider Payment")) return "Operator Payment";
  if (description.includes("Vehicle Payment")) return "Vehicle Payment";
  if (description.includes("Driver Payment")) return "Driver Payment";
  if (description.includes("SMS Job Rating")) return "Add Ons: SMS Rating";
  if (description.includes("Flight Tracking")) return "Add Ons: Flight Tracking";

  return description;
};

/**
 * Payment Attempts Table Helpers
 */
export const getPaymentAttemptsFromToText = (logType: string, category: string, paidTo?: string): string => {
  switch (logType) {
    case "client_payment":
      if (category === "refund") {
        return "Platform → Client";
      }
      return paidTo ? `Client → ${paidTo}` : "Client → Stripe";
    case "operator_refund":
      return paidTo ? `${paidTo} → Platform` : "Operator → Platform";
    case "operator_payment":
      return paidTo ? `${paidTo} → Stripe` : "Operator → Stripe";
    default:
      return "";
  }
};

/**
 * Paid Out Table Helpers
 */
export const getPaidOutPaymentType = (description: string): string => {
  return getPaymentTypeFromDescription(description);
};

export const getPaidOutFromToText = (type: string, description: string, operatorName: string, offloadedOperatorName?: string): string => {
  if (description.includes("reversal")) {
    if (description.includes("Driver Payment")) return "Stripe";
    if (description.includes("Provider Payment")) return "Provider → Stripe";
    if (description.includes("Platform Payment")) return "RideMinder → Stripe";
    return "Stripe";
  }

  if (description.includes("adjustment")) {
    if (description.includes("Platform Payment")) return "Stripe → RideMinder";
    if (description.includes("Driver Payment")) return "Stripe → Driver";
    if (description.includes("Provider Payment")) return `Stripe → ${operatorName}`;
    return "Stripe";
  }

  switch (type) {
    case "platform_transfer":
      if (description.includes("Credit Card Processing Fee") || description.includes("Stripe Bank Sweep Fee")) return "Stripe";

      return "Stripe → RideMinder";
    case "provider_transfer":
      if (description.includes("Vehicle Payment") && offloadedOperatorName) return `Stripe → ${offloadedOperatorName}`;

      return `Stripe → ${operatorName}`;
    case "driver_transfer":
      return "Stripe → Driver";
    default:
      return "";
  }
};

/**
 * Payment Ledger Table Helpers
 */
export const getPaymentCategory = (type: string): "Collected" | "Paid" | "Pending" => {
  if (type === "payment_out") return "Paid";
  if (type === "payment_in") return "Collected";
  return "Pending";
};

export const getLedgerPaymentType = (type: string, description: string, category: string): string => {
  if (description.includes("Payment") && category === "client" && type === "payment_in") {
    return "Client CC Charge";
  }

  if (description.includes("Payment") && category === "provider" && type === "payment_in") {
    return "Operator Bank Debit";
  }

  return getPaymentTypeFromDescription(description);
};

export const getLedgerFromToText = (type: string, description: string, category: string): string => {
  if (description.includes("reversal")) {
    if (description.includes("Driver Payment")) return "Stripe → Stripe";
    if (description.includes("Provider Payment")) return "Provider → Stripe";
    if (description.includes("Platform Payment")) return "Platform → Stripe";
    return "Stripe";
  }

  if (description.includes("adjustment")) {
    if (description.includes("Platform Payment")) return "Stripe → Platform";
    if (description.includes("Driver Payment")) return "Stripe → Driver";
    if (description.includes("Provider Payment")) return `Stripe → Operator`;
    return "Stripe";
  }

  if (type === "payment_in") {
    if (category === "client" || category === "driver" || category === "platform") {
      return "Client → Stripe";
    }
    if (category === "provider") return "Operator → Stripe";
    return "Client → Platform";
  }

  if (type === "payment_out") {
    if (category === "platform") return "Stripe → Platform";
    if (category === "driver") return "Stripe → Driver";
    if (category === "client") return "Platform → Client";
    if (category === "provider") {
      if (description?.includes("Driver Payment")) return "Stripe → Driver";
      return "Stripe → Operator";
    }
    return "Stripe → Platform";
  }

  return "";
};

/**
 * Refund Activity Table Helpers
 */
export const getRefundType = (type: string, description: string, category: string): string => {
  if (category === "client" && type === "payment_out") {
    return "Refunded to Client";
  }
  if (category === "provider" && type === "payment_in") {
    return "Charged to operator";
  }
  if (category === "platform" && type === "payment_out") {
    return "Stripe Refund Fee";
  }

  return description;
};

export const getRefundFromTo = (type: string, category: string): string => {
  if (category === "client" && type === "payment_out") {
    return "Platform → Client";
  }
  if (category === "provider" && type === "payment_in") {
    return "Operator → Platform";
  }
  if (category === "platform" && type === "payment_out") {
    return "Platform → Stripe";
  }

  return "";
};
